/**
 * Notification Polling Service for Android
 * Uses Local Notifications + Background Polling every 1 minute
 */

import api from './api';

class NotificationPollingService {
    constructor() {
        this.intervalId = null;
        this.lastNotificationIds = new Set();
        this.isRunning = false;
        this.pollInterval = 60 * 1000; // 1 minute
    }

    /**
     * Start the notification polling service
     */
    start() {
        if (this.isRunning) {
            return;
        }

        this.isRunning = true;

        // Initial fetch
        this.checkForNewNotifications();

        // Set interval for polling every 1 minute
        this.intervalId = setInterval(() => {
            this.checkForNewNotifications();
        }, this.pollInterval);

    }

    /**
     * Stop the polling service
     */
    stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isRunning = false;
    }

    /**
     * Check for new notifications from the server
     */
    async checkForNewNotifications() {
        try {


            const response = await api.get('/notifications?limit=10');
            const notifications = response.data.items || response.data || [];

            // Filter unread notifications
            const unreadNotifications = notifications.filter(n => !n.is_read);

            // Find new notifications (not in our cache)
            const newNotifications = unreadNotifications.filter(n => !this.lastNotificationIds.has(n.id));

            if (newNotifications.length > 0) {


                // Show local notifications for each new notification
                for (const notif of newNotifications) {
                    await this.showLocalNotification(notif);
                    this.lastNotificationIds.add(notif.id);
                }
            }

            // Update cache with current unread IDs
            this.lastNotificationIds = new Set(unreadNotifications.map(n => n.id));

        } catch (error) {
            console.error('[NotificationPolling] Error checking notifications:', error.message);
        }
    }

    /**
     * Show a local notification using Capacitor LocalNotifications
     */
    async showLocalNotification(notification) {
        try {
            // Check if we're in Capacitor environment
            if (window.Capacitor?.isNativePlatform()) {
                const { LocalNotifications } = window.Capacitor.Plugins;

                if (LocalNotifications) {
                    // Check and request permission if needed
                    let permResult = await LocalNotifications.checkPermissions();


                    if (permResult.display !== 'granted') {
                        permResult = await LocalNotifications.requestPermissions();
                    }

                    if (permResult.display !== 'granted') {
                        return;
                    }

                    // Generate unique numeric notification ID from UUID string
                    const hashCode = (str) => {
                        let hash = 0;
                        for (let i = 0; i < str.length; i++) {
                            hash = ((hash << 5) - hash) + str.charCodeAt(i);
                            hash |= 0; // Convert to 32-bit integer
                        }
                        return Math.abs(hash);
                    };
                    const notifId = hashCode(notification.id || String(Date.now()));

                    // Schedule the notification
                    const notifData = {
                        notifications: [
                            {
                                id: notifId,
                                title: this.getNotificationTitle(notification),
                                body: notification.message || notification.content || 'إشعار جديد',
                                largeBody: notification.message,
                                summaryText: 'EP-EG',
                                smallIcon: 'ic_notification',
                                iconColor: '#ea5f1f',
                                sound: 'default',
                                channelId: 'ep-eg-notifications',
                                autoCancel: true,
                                extra: {
                                    notificationId: notification.id,
                                    type: notification.type
                                }
                            }
                        ]
                    };

                    await LocalNotifications.schedule(notifData);
                } else {
                }
            } else {
                // Web fallback - use browser notifications
                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification(this.getNotificationTitle(notification), {
                        body: notification.message || notification.content || 'إشعار جديد',
                        icon: '/app-logo.png',
                        tag: `ep-eg-${notification.id}`,
                        requireInteraction: true
                    });
                }
            }
        } catch (error) {
            console.error('[NotificationPolling] Error showing notification:', error.message);
        }
    }

    /**
     * Get notification title based on type
     */
    getNotificationTitle(notification) {
        const titles = {
            'new_order': '🛒 طلب جديد',
            'order_approved': '✅ تمت الموافقة على الطلب',
            'order_rejected': '❌ تم رفض الطلب',
            'new_visit': '📍 زيارة جديدة',
            'expense_approved': '💰 تمت الموافقة على المصروف',
            'expense_rejected': '❌ تم رفض المصروف',
            'new_task': '📋 مهمة جديدة',
            'message': '💬 رسالة جديدة'
        };
        return titles[notification.type] || '🔔 إشعار جديد - EP-EG';
    }

    /**
     * Initialize notification channel for Android
     */
    async initializeChannel() {
        try {
            if (window.Capacitor?.isNativePlatform()) {
                const { LocalNotifications } = window.Capacitor.Plugins;

                if (LocalNotifications?.createChannel) {
                    await LocalNotifications.createChannel({
                        id: 'ep-eg-notifications',
                        name: 'إشعارات EP-EG',
                        description: 'إشعارات النظام والطلبات',
                        importance: 4, // HIGH
                        visibility: 1, // PUBLIC
                        sound: 'default',
                        vibration: true,
                        lights: true,
                        lightColor: '#ea5f1f'
                    });
                }
            }
        } catch (error) {
            console.error('[NotificationPolling] Channel creation error:', error.message);
        }
    }

    /**
     * Request notification permissions
     */
    async requestPermissions() {
        try {
            if (window.Capacitor?.isNativePlatform()) {
                const { LocalNotifications } = window.Capacitor.Plugins;
                if (LocalNotifications) {
                    const result = await LocalNotifications.requestPermissions();
                    return result.display === 'granted';
                }
            } else {
                // Web
                if ('Notification' in window) {
                    const result = await Notification.requestPermission();
                    return result === 'granted';
                }
            }
            return false;
        } catch (error) {
            console.error('[NotificationPolling] Permission request error:', error.message);
            return false;
        }
    }
}

// Create singleton instance
const notificationPollingService = new NotificationPollingService();

export default notificationPollingService;
